import { HeadingPriority } from "atoms/constants/heading-priority";
import Heading from "atoms/typography/heading";
import { UserSettingsKeys } from "constants/user-settings-keys";
import React, { useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useModalActions from "utilities/hooks/use-modal-actions";
import Image from "atoms/images/image";
import CasiLogo from "assets/images/casi-logo.png";
import Button from "atoms/buttons/button";
import CasiUserAgreementModal from "organisms/modals/casi-user-agreement-modal/casi-user-agreement-modal";
import { useLocalization } from "utilities/hooks/use-localization";
import TextInput from "atoms/forms/text-input";
import { useChatModalActions } from "utilities/hooks/domain/chat/use-chat-modal";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import StringUtils from "utilities/string-utils";
import useContainerWidth from "utilities/hooks/use-container-width";

interface Props {
    showTagline?: boolean;
    useCenteredCta?: boolean;
    useCenteredHeading?: boolean;
    useNoPaddingMain?: boolean;
    useRowLayout?: boolean;
    useSmallLogo?: boolean;
}

const TryCasiBanner = (props: Props) => {
    const {
        showTagline = true,
        useCenteredCta = false,
        useCenteredHeading = false,
        useSmallLogo = false,
        useRowLayout = false,
        useNoPaddingMain = false,
    } = props;
    const cssBaseClass = "c-try-casi";
    const enableAIChat =
        useGlobalState().globalState.getUserSettingValue(
            UserSettingsKeys.EnableAIChat
        ) ?? false;

    const { handleOpen, handleClose, isOpen } = useModalActions();
    const { ref } = useContainerWidth({ breakpoint: 750 });

    const className = useRowLayout ? cssBaseClass + " -row" : cssBaseClass;

    return (
        <>
            <div ref={ref} className={className}>
                <TryCasiBannerHeader
                    cssModifier={useCenteredHeading ? " -center" : ""}
                />
                <div
                    className={`${cssBaseClass}__main${useNoPaddingMain ? " -no-padding" : ""}`}>
                    {showTagline && <TryCasiBannerTagline />}

                    <TryCasiBannerLogo
                        cssModifier={useSmallLogo ? " -small" : ""}
                    />
                </div>

                {!enableAIChat && (
                    <TryCasiBannerCta
                        handleOpen={handleOpen}
                        cssModifier={useCenteredCta ? " -center" : ""}
                    />
                )}

                {enableAIChat && <TryCasiBannerPrompt />}
            </div>

            <CasiUserAgreementModal isOpen={isOpen} onClose={handleClose} />
        </>
    );
};

export default TryCasiBanner;

interface ChildProps {
    cssModifier: string;
}

const TryCasiBannerHeader = ({ cssModifier }: ChildProps) => {
    const className = "c-try-casi__header" + cssModifier;
    const { t } = useLocalization();

    return (
        <Heading priority={HeadingPriority.Three} cssClassName={className}>
            {t("newAiExperience")}
        </Heading>
    );
};

const TryCasiBannerTagline = () => {
    const cssBaseClass = "c-try-casi__tagline";
    const { t } = useLocalization();
    const casiTagline = t("casiTagline").split("\n");

    return (
        <Heading priority={HeadingPriority.Five} cssClassName={cssBaseClass}>
            {casiTagline[0]}
            <br />
            {casiTagline[1]}
            <br />
            <span className="-accent">{casiTagline[2]}</span>
        </Heading>
    );
};

const TryCasiBannerLogo = ({ cssModifier }: ChildProps) => {
    const className = "c-try-casi__logo" + cssModifier;
    return (
        <Image altText="CASI logo" src={CasiLogo} cssClassName={className} />
    );
};

interface CtaProps extends ChildProps {
    handleOpen: () => void;
}

const TryCasiBannerCta = ({ handleOpen, cssModifier }: CtaProps) => {
    const className = "c-try-casi__cta" + cssModifier;
    const { t } = useLocalization();

    return (
        <div className={className}>
            <Button onClick={handleOpen}>{t("tryCasi")}</Button>
        </div>
    );
};

const TryCasiBannerPrompt = () => {
    const cssBaseClass = "c-try-casi__prompt";
    const { t } = useLocalization();
    const { setStartingPrompt, openChatModal } = useChatModalActions();
    const [promptText, setPromptText] = useState("");

    const isDisabled = !StringUtils.hasValue(promptText);

    const handleSubmit = () => {
        setStartingPrompt(promptText);
        openChatModal();
        setPromptText("");
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !isDisabled) {
            handleSubmit();
        }
    };

    return (
        <div className={cssBaseClass}>
            <TextInput
                id={"casi-banner-prompt-input"}
                cssClassName={`${cssBaseClass}__input`}
                onChange={(e) => setPromptText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("tryCasiPlaceholder")}
                value={promptText}
            />
            <Button
                accessibleText={t("tryCasiPlaceholder")}
                cssClassName={`${cssBaseClass}__send -override`}
                onClick={handleSubmit}
                onKeyDown={handleKeyDown}
                size={ButtonSizes.Small}
                style={ButtonStyles.RoundedPrimary}
                disabled={isDisabled}>
                <Icon type={Icons.SendArrow} size={IconSizes.Small} />
            </Button>
        </div>
    );
};
